import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageToast from "sap/m/MessageToast";
import MessageBox from "sap/m/MessageBox";
import AuthService from "../service/AuthService";
import SettingsService from "../service/SettingsService";
import type { EmailConfig } from "../service/SettingsService";
import Event from "sap/ui/base/Event";
import Item from "sap/ui/core/Item";
import EventBus from "sap/ui/core/EventBus";
import Control from "sap/ui/core/Control";
import Dialog from "sap/m/Dialog";
import type { IntegrationRecord } from "../service/KeyManagementService";
import KeyManagementService from "../service/KeyManagementService";
import View from "sap/ui/core/mvc/View";
import type ConnectedApps from "./ConnectedApps.controller";

const PASSWORD_SENTINEL = "••••••••";

interface KeyManagementPageModel {
	items: IntegrationRecord[];
	busy: boolean;
}

interface KeyManagementEditModel {
	name: string;
}

function createEmptyKeyEditModel(): KeyManagementEditModel {
	return { name: "" };
}

/**
 * @namespace com.ang.portal.controller
 */
export default class Settings extends BaseController {
	public onInit(): void {
		// View is embedded in the Home shell; refreshPage() is invoked by Home on side-nav selection.
	}

	public refreshPage(): void {
		if (!AuthService.isAuthenticated()) {
			this.navTo("login", {}, true);
			return;
		}

		const userInfo = AuthService.getUserInfo();
		if (!userInfo?.roles.includes("system:admin")) {
			this.navTo("home", {}, true);
			return;
		}

		// Initialize edit model with placeholders; the SAP SL fields are populated
		// from /api/settings (system:admin only) below. The global "settings"
		// model only contains public branding (title/logoUrl) and does not
		// include SL credentials.
		const oEditModel = new JSONModel({
			title: "Portal",
			logoUrl: "",
			// Login page copy; empty means the i18n bundle default is used
			loginWelcomeTitle: "",
			loginWelcomeText: "",
			loginFooterText: "",
			// Defaults to on so a failed settings load never presents SSO as
			// disabled when it is not.
			ssoEnabled: true,
			// Sign-in policy; defaults mirror the migration (password on, 2FA optional).
			passwordLoginEnabled: true,
			twoFactorRequired: false,
			// Colour instance. Empty means "the theme JSON's default", which is
			// also what an untouched company record holds.
			themeInstance: "",
			// Filled from theme-instances.json once it loads; the JSON is the
			// single source of the option list.
			themeInstances: [] as Array<{ key: string; text: string }>,
			slUrl: "",
			apiGatewayUrl: "",
			slCompanyDb: "",
			slUsername: "",
			slPassword: "",
			slConnected: false,
			slStatusLoading: true,
			// Email config fields
			emailAuthMode: "delegated",
			emailSenderEmail: "",
			emailTenantId: "",
			emailClientId: "",
			emailClientSecret: "",
			mailboxConnected: false,
			connectedMailboxEmail: "",
			connectedMailboxName: "",
			emailConfigured: false,
			emailConnectLoading: false,
			emailTestLoading: false,
			// Connect, test and verify all act on the SAVED configuration, so the
			// form must be able to say when it has drifted from it.
			emailDirty: false,
			// Application-mode access check: point-in-time, never persisted.
			emailVerifyLoading: false,
			emailVerifyState: "",
			emailVerifyText: "",
			// Switching to application mode persists on its own, so the user does
			// not have to leave the page through the global Save to reach Verify.
			emailAutoSaveLoading: false,
		});
		this.setModel(oEditModel, "settingsEdit");

		// JSONModel fires propertyChange for every two-way update from a control,
		// so one listener covers all five email inputs.
		oEditModel.attachPropertyChange((oEvent) => {
			const sPath = oEvent.getParameter("path" as never) as string;
			if (typeof sPath === "string" && sPath.startsWith("/email")) {
				this.refreshEmailDirty();
			}
		});
		void this.handleMailboxConnectResult();

		this.setModel(new JSONModel({ selectedTab: "config" }), "settingsPage");
		this.setModel(
			new JSONModel({ items: [], busy: false } as KeyManagementPageModel),
			"keyManagementPage",
		);
		this.setModel(new JSONModel(createEmptyKeyEditModel()), "keyManagementEdit");
		this.setModel(new JSONModel({ apiKey: "" }), "keyManagementReveal");
		void this.loadKeyIntegrations();
		void this.loadThemeInstances();

		// Load full settings (branding + SAP SL credentials) for admin editing.
		void SettingsService.getSettings()
			.then((settings) => {
				oEditModel.setProperty("/title", settings.title || "Portal");
				oEditModel.setProperty("/logoUrl", settings.logoUrl || "");
				oEditModel.setProperty(
					"/loginWelcomeTitle",
					settings.loginWelcomeTitle || "",
				);
				oEditModel.setProperty(
					"/loginWelcomeText",
					settings.loginWelcomeText || "",
				);
				oEditModel.setProperty(
					"/loginFooterText",
					settings.loginFooterText || "",
				);
				oEditModel.setProperty("/ssoEnabled", settings.ssoEnabled !== false);
				oEditModel.setProperty(
					"/passwordLoginEnabled",
					settings.passwordLoginEnabled !== false,
				);
				oEditModel.setProperty(
					"/twoFactorRequired",
					settings.twoFactorRequired === true,
				);
				oEditModel.setProperty("/themeInstance", settings.themeInstance || "");
				oEditModel.setProperty("/slUrl", settings.slUrl || "");
				oEditModel.setProperty("/apiGatewayUrl", settings.apiGatewayUrl || "");
				oEditModel.setProperty("/slCompanyDb", settings.slCompanyDb || "");
				oEditModel.setProperty("/slUsername", settings.slUsername || "");
				oEditModel.setProperty(
					"/slPassword",
					settings.slPasswordSet ? PASSWORD_SENTINEL : "",
				);
			})
			.catch(() => {
				// Keep defaults on error
			});

		// Load SL connection status
		void SettingsService.getSlStatus()
			.then((status) => {
				oEditModel.setProperty("/slConnected", status.connected);
			})
			.catch(() => {
				// Keep disconnected on error
			})
			.finally(() => {
				oEditModel.setProperty("/slStatusLoading", false);
			});

		// Load email config
		void SettingsService.getEmailConfig()
			.then((config) => {
				oEditModel.setProperty("/emailAuthMode", config.authMode);
				oEditModel.setProperty("/emailSenderEmail", config.senderEmail || "");
				oEditModel.setProperty("/emailTenantId", config.tenantId || "");
				oEditModel.setProperty("/emailClientId", config.clientId || "");
				oEditModel.setProperty(
					"/emailClientSecret",
					config.clientSecretSet ? PASSWORD_SENTINEL : "",
				);
				oEditModel.setProperty("/mailboxConnected", config.mailboxConnected);
				oEditModel.setProperty(
					"/connectedMailboxEmail",
					config.connectedMailboxEmail || "",
				);
				oEditModel.setProperty(
					"/connectedMailboxName",
					config.connectedMailboxName || "",
				);
				oEditModel.setProperty("/emailConfigured", config.configured);
				this.snapshotEmailConfig();
			})
			.catch(() => {
				// Keep defaults on error
			});

	}

	public onLogoFileChange(oEvent: Event): void {
		const aFiles = oEvent.getParameter("files" as never) as FileList;
		if (!aFiles || aFiles.length === 0) return;

		const oReader = new FileReader();
		oReader.onload = () => {
			const oModel = this.getModel("settingsEdit") as JSONModel;
			oModel.setProperty("/logoUrl", oReader.result as string);
		};
		oReader.readAsDataURL(aFiles[0]);
	}

	/**
	 * Fill the colour-theme dropdown from theme-instances.json.
	 *
	 * The option list is read from the same file the loader reads, so adding an
	 * instance there is the only change needed to offer it here. A failure
	 * leaves the Select empty rather than blocking the page - every other
	 * setting on it still works.
	 */
	private async loadThemeInstances(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		try {
			const response = await fetch(
				sap.ui.require.toUrl("com/ang/portal/theme/theme-instances.json"),
				{ cache: "no-cache" },
			);
			if (!response.ok) return;
			const config = (await response.json()) as object;
			oEditModel.setProperty("/themeInstances", AngTheme.instances(config));
		} catch {
			// Leave the list empty; the saved value is still shown and kept.
		}
	}

	/**
	 * Preview the chosen instance immediately, before Save.
	 *
	 * The colours are inline custom properties, so re-applying repaints at once
	 * and costs one small fetch. Picking a theme you cannot see until after a
	 * save-and-reload would make this a guessing game.
	 *
	 * ⚠️ This changes the LIVE appearance without persisting. Leaving the page
	 * without saving therefore leaves the preview showing until the next full
	 * load, when the stored value wins again.
	 */
	public onThemeInstanceChange(): void {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const instance = oEditModel.getProperty("/themeInstance") as string;
		const component = this.getOwnerComponent() as unknown as {
			applyTheme(id?: string): Promise<void>;
		};
		void component.applyTheme(instance);
	}

	public onRemoveLogo(): void {
		const oModel = this.getModel("settingsEdit") as JSONModel;
		oModel.setProperty("/logoUrl", "");
	}

	public async onSave(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const title = oEditModel.getProperty("/title") as string;
		const logoUrl = oEditModel.getProperty("/logoUrl") as string;
		const loginWelcomeTitle = oEditModel.getProperty(
			"/loginWelcomeTitle",
		) as string;
		const loginWelcomeText = oEditModel.getProperty(
			"/loginWelcomeText",
		) as string;
		const loginFooterText = oEditModel.getProperty("/loginFooterText") as string;
		const ssoEnabled = oEditModel.getProperty("/ssoEnabled") as boolean;
		const passwordLoginEnabled = oEditModel.getProperty(
			"/passwordLoginEnabled",
		) as boolean;
		const twoFactorRequired = oEditModel.getProperty(
			"/twoFactorRequired",
		) as boolean;
		const themeInstance = oEditModel.getProperty("/themeInstance") as string;
		const slUrl = oEditModel.getProperty("/slUrl") as string;
		const apiGatewayUrl = oEditModel.getProperty("/apiGatewayUrl") as string;
		const slCompanyDb = oEditModel.getProperty("/slCompanyDb") as string;
		const slUsername = oEditModel.getProperty("/slUsername") as string;
		const slPasswordRaw = oEditModel.getProperty("/slPassword") as string;

		// Only send password if user changed it from the sentinel
		const slPassword =
			slPasswordRaw !== PASSWORD_SENTINEL ? slPasswordRaw : undefined;

		// Email config fields
		const emailAuthMode = oEditModel.getProperty(
			"/emailAuthMode",
		) as "application" | "delegated";
		const emailSenderEmail = (
			oEditModel.getProperty("/emailSenderEmail") as string
		).trim();
		const emailTenantId = oEditModel.getProperty("/emailTenantId") as string;
		const emailClientId = oEditModel.getProperty("/emailClientId") as string;
		const emailClientSecretRaw = oEditModel.getProperty(
			"/emailClientSecret",
		) as string;
		const emailClientSecret =
			emailClientSecretRaw !== PASSWORD_SENTINEL
				? emailClientSecretRaw
				: undefined;

		try {
			const updated = await SettingsService.updateSettings({
				title,
				logoUrl,
				loginWelcomeTitle,
				loginWelcomeText,
				loginFooterText,
				ssoEnabled,
				passwordLoginEnabled,
				twoFactorRequired,
				themeInstance,
				slUrl,
				apiGatewayUrl,
				slCompanyDb,
				slUsername,
				...(slPassword !== undefined ? { slPassword } : {}),
			});

			const oSettingsModel = this.getOwnerComponent().getModel(
				"settings",
			) as JSONModel;
			oSettingsModel.setData(updated);

			// Save email config if fields are filled
			if (emailTenantId && emailClientId) {
				// Application mode sends as a fixed mailbox, so that address is required.
				if (emailAuthMode === "application" && !emailSenderEmail) {
					const bundle = await this.getResourceBundle();
					MessageToast.show(
						bundle.getText("settingsEmailSenderEmailRequired") ?? "",
					);
					return;
				}
				const emailConfig = await SettingsService.updateEmailConfig({
					authMode: emailAuthMode,
					tenantId: emailTenantId,
					clientId: emailClientId,
					...(emailClientSecret ? { clientSecret: emailClientSecret } : {}),
					...(emailAuthMode === "application"
						? { senderEmail: emailSenderEmail }
						: {}),
				});
				// The form now matches the server, so testing is allowed again.
				this.applyEmailConfigToModel(emailConfig);
			}

			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("settingsSaveSuccess"));
			this.returnToHome();
		} catch (error) {
			// Carries the server's reason when it refused the change, e.g. password
			// sign-in turned off while Microsoft sign-in is not configured.
			const bundle = await this.getResourceBundle();
			MessageToast.show(
				error instanceof Error && error.message
					? error.message
					: bundle.getText("settingsSaveError"),
			);
		}
	}

	private async handleMailboxConnectResult(): Promise<void> {
		const params = new URLSearchParams(window.location.search);
		const status = params.get("mailboxConnect");
		if (!status) {
			return;
		}

		const message = params.get("mailboxMessage") || "";
		const bundle = await this.getResourceBundle();

		if (status === "success") {
			MessageToast.show(
				bundle.getText("settingsEmailConnectSuccess", [message || "Microsoft"]),
			);
		} else {
			MessageToast.show(message || bundle.getText("settingsEmailConnectError"));
		}

		params.delete("mailboxConnect");
		params.delete("mailboxMessage");
		const query = params.toString();
		const cleanUrl = `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
		window.history.replaceState({}, document.title, cleanUrl);
	}

	private returnToHome(): void {
		EventBus.getInstance().publish("HomeChannel", "NavigateHome", {});
	}

	public onSlFieldChange(): void {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		oEditModel.setProperty("/slConnected", false);
	}

	public async onSlConnect(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		oEditModel.setProperty("/slStatusLoading", true);

		const slUrl = oEditModel.getProperty("/slUrl") as string;
		const slCompanyDb = oEditModel.getProperty("/slCompanyDb") as string;
		const slUsername = oEditModel.getProperty("/slUsername") as string;
		const slPasswordRaw = oEditModel.getProperty("/slPassword") as string;
		const slPassword =
			slPasswordRaw !== PASSWORD_SENTINEL ? slPasswordRaw : undefined;

		try {
			// Save credentials to backend first so the connect endpoint can use them
			const updated = await SettingsService.updateSettings({
				slUrl,
				slCompanyDb,
				slUsername,
				...(slPassword !== undefined ? { slPassword } : {}),
			});

			const oSettingsModel = this.getOwnerComponent().getModel(
				"settings",
			) as JSONModel;
			oSettingsModel.setData(updated);

			const status = await SettingsService.connectSl();
			oEditModel.setProperty("/slConnected", status.connected);

			const bundle = await this.getResourceBundle();
			if (status.connected) {
				MessageToast.show(bundle.getText("settingsSlConnectSuccess"));
			} else {
				MessageToast.show(bundle.getText("settingsSlConnectError"));
			}
		} catch (err) {
			const bundle = await this.getResourceBundle();
			const message =
				err instanceof Error
					? err.message
					: bundle.getText("settingsSlConnectError");
			MessageToast.show(message);
		} finally {
			oEditModel.setProperty("/slStatusLoading", false);
		}
	}

	public async onEmailConnect(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const emailTenantId = oEditModel.getProperty("/emailTenantId") as string;
		const emailClientId = oEditModel.getProperty("/emailClientId") as string;
		const emailClientSecretRaw = oEditModel.getProperty(
			"/emailClientSecret",
		) as string;
		const emailClientSecret =
			emailClientSecretRaw !== PASSWORD_SENTINEL
				? emailClientSecretRaw
				: undefined;

		if (!emailTenantId || !emailClientId || !emailClientSecretRaw) {
			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("settingsEmailMissingFields"));
			return;
		}

		oEditModel.setProperty("/emailConnectLoading", true);

		try {
			const emailConfig = await SettingsService.updateEmailConfig({
				authMode: "delegated",
				tenantId: emailTenantId,
				clientId: emailClientId,
				...(emailClientSecret ? { clientSecret: emailClientSecret } : {}),
			});

			// Connect persists the config too, so the form is clean again.
			this.applyEmailConfigToModel(emailConfig);

			const { authorizationUrl } =
				await SettingsService.getEmailMailboxConnectUrl();
			window.location.assign(authorizationUrl);
		} catch (err) {
			const bundle = await this.getResourceBundle();
			const message =
				err instanceof Error
					? err.message
					: bundle.getText("settingsEmailConnectError");
			MessageToast.show(message);
			oEditModel.setProperty("/emailConnectLoading", false);
		}
	}

	/**
	 * Adopt the configuration the server just returned, then snapshot it.
	 *
	 * Every caller means the same thing by this - "the form now matches the
	 * server" - so the snapshot belongs here rather than at each call site, where
	 * it could be taken before the write-back and record a stale value.
	 *
	 * authMode and senderEmail are written back deliberately: a delegated save
	 * clears sender_email server-side, and without adopting that the form would
	 * keep showing the address the user typed and snapshot it as clean.
	 */
	private applyEmailConfigToModel(emailConfig: EmailConfig): void {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;

		oEditModel.setProperty("/emailConfigured", emailConfig.configured);
		oEditModel.setProperty("/emailAuthMode", emailConfig.authMode);
		oEditModel.setProperty("/emailSenderEmail", emailConfig.senderEmail || "");
		oEditModel.setProperty("/mailboxConnected", emailConfig.mailboxConnected);
		oEditModel.setProperty(
			"/connectedMailboxEmail",
			emailConfig.connectedMailboxEmail || "",
		);
		oEditModel.setProperty(
			"/connectedMailboxName",
			emailConfig.connectedMailboxName || "",
		);
		oEditModel.setProperty(
			"/emailClientSecret",
			emailConfig.clientSecretSet ? PASSWORD_SENTINEL : "",
		);
		this.snapshotEmailConfig();
	}

	/**
	 * May a bare mode switch be persisted on its own?
	 *
	 * The endpoint is a full-row upsert, not a patch, so an unguarded auto-save
	 * would also commit whatever else sits half-typed in the form - a partial
	 * tenant id, a client secret mid-paste. Hence the drift check: only a change
	 * to the mode and the sender address it requires may travel this way.
	 *
	 * Application direction only. Saving with authMode "delegated" makes the
	 * backend null the refresh token and disconnect the mailbox, which must never
	 * happen as a side effect of touching a dropdown.
	 */
	private canAutoSaveEmailConfig(sTargetMode: string): boolean {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;

		if (sTargetMode !== "application") {
			return false;
		}

		// Update-only: creating a row requires a client secret, so first-time
		// setup stays an explicit Save.
		if (!oEditModel.getProperty("/emailConfigured")) {
			return false;
		}

		const oSnapshot = oEditModel.getProperty("/emailSavedSnapshot") as
			| Record<string, string>
			| undefined;
		if (!oSnapshot) {
			return false;
		}

		const oCurrent = this.readEmailFields();
		const aDrifted = Object.keys(oCurrent).filter(
			(sKey) => (oCurrent[sKey] || "") !== (oSnapshot[sKey] || ""),
		);
		if (!aDrifted.length) {
			return false;
		}
		if (
			aDrifted.some((sKey) => sKey !== "authMode" && sKey !== "senderEmail")
		) {
			return false;
		}

		// Mirrors the backend gate: tenantId and clientId are always required,
		// senderEmail additionally in application mode.
		return (
			!!(oEditModel.getProperty("/emailTenantId") as string) &&
			!!(oEditModel.getProperty("/emailClientId") as string) &&
			!!((oEditModel.getProperty("/emailSenderEmail") as string) || "").trim()
		);
	}

	/**
	 * Persist just the email configuration, in place. Unlike onSave this saves no
	 * other section and does not navigate home - the point is that the user stays
	 * on the page with Verify Access now enabled.
	 */
	private async autoSaveEmailConfig(sTargetMode: string): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;

		// Rapid toggling must not queue a second write.
		if (oEditModel.getProperty("/emailAutoSaveLoading")) {
			return;
		}
		if (!this.canAutoSaveEmailConfig(sTargetMode)) {
			return;
		}

		oEditModel.setProperty("/emailAutoSaveLoading", true);

		try {
			// clientSecret is omitted on purpose: the drift check proved it is
			// unchanged, and omitting it makes the backend keep the stored one.
			const emailConfig = await SettingsService.updateEmailConfig({
				authMode: "application",
				tenantId: oEditModel.getProperty("/emailTenantId") as string,
				clientId: oEditModel.getProperty("/emailClientId") as string,
				senderEmail: (
					oEditModel.getProperty("/emailSenderEmail") as string
				).trim(),
			});

			this.applyEmailConfigToModel(emailConfig);

			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("settingsEmailModeSaved"));
		} catch (err) {
			const bundle = await this.getResourceBundle();
			// The selection is left as the user set it: reverting would fight the
			// two-way binding, and the dirty strip already explains the state.
			MessageToast.show(
				err instanceof Error
					? err.message
					: bundle.getText("settingsEmailSaveError"),
			);
		} finally {
			oEditModel.setProperty("/emailAutoSaveLoading", false);
		}
	}

	/** Mode switched in the dropdown - the new key comes from the event. */
	public async onEmailModeChange(oEvent: Event): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const oSelectedItem = oEvent.getParameter(
			"selectedItem" as never,
		) as Item | null;
		const sMode =
			oSelectedItem?.getKey() ||
			(oEditModel.getProperty("/emailAuthMode") as string);

		await this.autoSaveEmailConfig(sMode);
	}

	/**
	 * Sender email completed. Application mode cannot be saved without it, so the
	 * field that was missing gets its own chance to finish the job - whichever of
	 * the two the user fills in last is the one that persists the config.
	 */
	public async onEmailSenderEmailChange(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;

		await this.autoSaveEmailConfig(
			oEditModel.getProperty("/emailAuthMode") as string,
		);
	}

	/**
	 * The five fields that make up the saved email configuration. Compared as a
	 * set so restoring an edited value clears the dirty flag, rather than a
	 * one-way "touched" marker that would keep the test button disabled.
	 */
	private readEmailFields(): Record<string, string> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		return {
			authMode: oEditModel.getProperty("/emailAuthMode") as string,
			tenantId: oEditModel.getProperty("/emailTenantId") as string,
			clientId: oEditModel.getProperty("/emailClientId") as string,
			clientSecret: oEditModel.getProperty("/emailClientSecret") as string,
			senderEmail: oEditModel.getProperty("/emailSenderEmail") as string,
		};
	}

	/** Record the state that is now on the server, and clear the dirty flag. */
	private snapshotEmailConfig(): void {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		oEditModel.setProperty("/emailSavedSnapshot", this.readEmailFields());
		oEditModel.setProperty("/emailDirty", false);
	}

	private refreshEmailDirty(): void {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const oSnapshot = oEditModel.getProperty("/emailSavedSnapshot") as
			| Record<string, string>
			| undefined;

		// Before the first load there is nothing to differ from.
		if (!oSnapshot) {
			return;
		}

		const oCurrent = this.readEmailFields();
		const bDirty = Object.keys(oCurrent).some(
			(sKey) => (oCurrent[sKey] || "") !== (oSnapshot[sKey] || ""),
		);
		oEditModel.setProperty("/emailDirty", bDirty);

		// Any edit invalidates a previous verification: it described the saved
		// configuration, which is no longer what is on screen.
		if (bDirty) {
			oEditModel.setProperty("/emailVerifyState", "");
			oEditModel.setProperty("/emailVerifyText", "");
		}
	}

	/**
	 * Check that application-mode credentials can send as the configured sender.
	 * Runs against the saved config, so the button is disabled while dirty.
	 */
	public async onEmailVerify(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const bundle = await this.getResourceBundle();

		oEditModel.setProperty("/emailVerifyLoading", true);

		try {
			const result = await SettingsService.verifyEmailConfig();
			oEditModel.setProperty("/emailVerifyState", result.level);
			oEditModel.setProperty(
				"/emailVerifyText",
				result.level === "success"
					? bundle.getText("settingsEmailVerifiedAs", [
							result.mailbox || result.message,
						])
					: result.message,
			);
		} catch (err) {
			oEditModel.setProperty("/emailVerifyState", "error");
			oEditModel.setProperty(
				"/emailVerifyText",
				err instanceof Error
					? err.message
					: bundle.getText("settingsEmailVerifyError"),
			);
		} finally {
			oEditModel.setProperty("/emailVerifyLoading", false);
		}
	}

	public async onEmailTest(): Promise<void> {
		const oEditModel = this.getModel("settingsEdit") as JSONModel;
		const bundle = await this.getResourceBundle();

		// Each mode has its own precondition: delegated needs a connected
		// mailbox, application needs a sender address. Checking mailboxConnected
		// for both would block a correctly configured application setup.
		const authMode = oEditModel.getProperty("/emailAuthMode") as string;

		if (authMode === "delegated") {
			if (!(oEditModel.getProperty("/mailboxConnected") as boolean)) {
				MessageToast.show(bundle.getText("settingsEmailMailboxNotConnected"));
				return;
			}
		} else if (
			!((oEditModel.getProperty("/emailSenderEmail") as string) || "").trim()
		) {
			MessageToast.show(bundle.getText("settingsEmailSenderEmailRequired"));
			return;
		}

		// The test reads the saved row, so unsaved edits would test the wrong
		// configuration - which is exactly how a delegated config got tested
		// while the form showed application mode.
		if (oEditModel.getProperty("/emailDirty") as boolean) {
			MessageToast.show(bundle.getText("settingsEmailSaveBeforeTest"));
			return;
		}

		oEditModel.setProperty("/emailTestLoading", true);

		try {
			const userInfo = AuthService.getUserInfo();
			const defaultEmail = userInfo?.email || "";

			// The backend rejects an empty recipient with a generic 400; say so
			// here instead, where the reason is knowable.
			if (!defaultEmail) {
				MessageToast.show(bundle.getText("settingsEmailNoRecipient"));
				oEditModel.setProperty("/emailTestLoading", false);
				return;
			}

			// Name the recipient rather than promising "your email address" -
			// there is no input, so this is the only place it can be seen.
			MessageBox.show(bundle.getText("settingsEmailTestPrompt", [defaultEmail]), {
				icon: MessageBox.Icon.QUESTION,
				title: bundle.getText("settingsEmailTest"),
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				initialFocus: MessageBox.Action.CANCEL,
				onClose: (action) => {
					if (action !== "OK") {
						oEditModel.setProperty("/emailTestLoading", false);
						return;
					}
					void (async () => {
						try {
							const result =
								await SettingsService.testEmailConfig(defaultEmail);
							MessageToast.show(
								bundle.getText("settingsEmailTestSuccess", [result.sentTo]),
							);
						} catch (err) {
							const message =
								err instanceof Error
									? err.message
									: bundle.getText("settingsEmailTestError");
							MessageToast.show(message);
						} finally {
							oEditModel.setProperty("/emailTestLoading", false);
						}
					})();
				},
			});
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: bundle.getText("settingsEmailSaveError");
			MessageToast.show(message);
			oEditModel.setProperty("/emailTestLoading", false);
		}
	}

	public onCancel(): void {
		this.returnToHome();
	}

	// ── Key Management tab ────────────────────────────────────────────

	public onSettingsTabSelect(oEvent: Event): void {
		const key = oEvent.getParameter("key" as never) as string;
		(this.getModel("settingsPage") as JSONModel).setProperty(
			"/selectedTab",
			key,
		);
		if (key === "connectedApps") {
			const nested = this.byId("connectedAppsView") as View | undefined;
			void (nested?.getController() as ConnectedApps | undefined)?.refreshPage();
		}
	}

	private getKeyManagementPageModel(): JSONModel {
		return this.getModel("keyManagementPage") as JSONModel;
	}

	private getKeyManagementEditModel(): JSONModel {
		return this.getModel("keyManagementEdit") as JSONModel;
	}

	private getKeyManagementRevealModel(): JSONModel {
		return this.getModel("keyManagementReveal") as JSONModel;
	}

	private getIntegrationFromEvent(oEvent: Event): IntegrationRecord | null {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) {
			return null;
		}

		const raw = source
			.getBindingContext("keyManagementPage")
			?.getObject() as unknown;
		if (!raw || typeof raw !== "object") {
			return null;
		}

		return raw as IntegrationRecord;
	}

	private async loadKeyIntegrations(): Promise<void> {
		const pageModel = this.getKeyManagementPageModel();
		pageModel.setProperty("/busy", true);
		try {
			const items = await KeyManagementService.getIntegrations();
			pageModel.setProperty("/items", items);
		} catch (error) {
			await this.showToastForError(error, "keyManagementLoadError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public onOpenCreateDialog(): void {
		this.getKeyManagementEditModel().setData(createEmptyKeyEditModel());
		(this.byId("keyIntegrationCreateDialog") as Dialog).open();
	}

	public onCloseCreateDialog(): void {
		(this.byId("keyIntegrationCreateDialog") as Dialog).close();
	}

	public async onSaveIntegration(): Promise<void> {
		const editModel = this.getKeyManagementEditModel();
		const data = editModel.getData() as KeyManagementEditModel;

		if (!data.name.trim()) {
			await this.showToast("keyManagementValidationRequired");
			return;
		}

		const pageModel = this.getKeyManagementPageModel();
		pageModel.setProperty("/busy", true);
		try {
			const created = await KeyManagementService.createIntegration({
				name: data.name.trim(),
			});
			this.onCloseCreateDialog();
			await this.loadKeyIntegrations();
			this.openRevealDialog(created.apiKey);
		} catch (error) {
			await this.showToastForError(error, "keyManagementCreateError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public async onRotateKeyPress(oEvent: Event): Promise<void> {
		const integration = this.getIntegrationFromEvent(oEvent);
		if (!integration) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(
				bundle.getText("keyManagementRotateConfirm", [integration.name]),
				{
					actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
					emphasizedAction: MessageBox.Action.OK,
					onClose: (action) => resolve(action === "OK"),
				},
			);
		});

		if (!confirmed) {
			return;
		}

		const pageModel = this.getKeyManagementPageModel();
		pageModel.setProperty("/busy", true);
		try {
			const result = await KeyManagementService.rotateIntegration(
				integration.id,
			);
			await this.loadKeyIntegrations();
			this.openRevealDialog(result.apiKey);
		} catch (error) {
			await this.showToastForError(error, "keyManagementRotateError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public async onRevokeKeyPress(oEvent: Event): Promise<void> {
		const integration = this.getIntegrationFromEvent(oEvent);
		if (!integration) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(
				bundle.getText("keyManagementRevokeConfirm", [integration.name]),
				{
					actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
					emphasizedAction: MessageBox.Action.OK,
					onClose: (action) => resolve(action === "OK"),
				},
			);
		});

		if (!confirmed) {
			return;
		}

		const pageModel = this.getKeyManagementPageModel();
		pageModel.setProperty("/busy", true);
		try {
			await KeyManagementService.revokeIntegration(integration.id);
			await this.showToast("keyManagementRevokeSuccess");
			await this.loadKeyIntegrations();
		} catch (error) {
			await this.showToastForError(error, "keyManagementRevokeError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	// --- One-time key reveal ---

	private openRevealDialog(apiKey: string): void {
		this.getKeyManagementRevealModel().setProperty("/apiKey", apiKey);
		(this.byId("keyIntegrationRevealDialog") as Dialog).open();
	}

	public onCloseRevealDialog(): void {
		(this.byId("keyIntegrationRevealDialog") as Dialog).close();
		// Never let the raw key linger in memory once the dialog is dismissed.
		this.getKeyManagementRevealModel().setProperty("/apiKey", "");
	}

	public async onCopyRevealedKey(): Promise<void> {
		const apiKey = this.getKeyManagementRevealModel().getProperty(
			"/apiKey",
		) as string;
		if (!apiKey) {
			return;
		}
		try {
			await navigator.clipboard.writeText(apiKey);
			await this.showToast("keyManagementKeyCopied");
		} catch {
			MessageToast.show(apiKey);
		}
	}
}
